import React, { useEffect, useState } from 'react';
import Select from 'react-select';

/**
 * react-select writes `z-index: 1` inline on the portal element, which beats the
 * `.cag-select__menu-portal` stylesheet rule. The portal is a direct child of
 * `body`, so any overlay that stacks above 1 — such as a costing card expanded to
 * full page at `z-index: 10000` — paints over an otherwise correctly positioned
 * menu and the options look missing. 10000 is the highest z-index in the app, so
 * the portal has to clear it.
 */
const MENU_PORTAL_STYLES = { menuPortal: (base) => ({ ...base, zIndex: 10050 }) };

/**
 * react-select dropdown cell.
 *
 * cellRendererParams (merged with column.cellProps):
 *   options              array
 *   getOptionLabel(opt)  default opt.label
 *   getOptionValue(opt)  default opt.value
 *   onRowChange(rowId, field, value)
 *   getRowId(row) -> string
 *   placeholder          falls back to the column's headerName, then 'Dropdown'
 *   disabled
 *   isClearable
 */
function SelectInputCell(params) {
  const {
    value,
    data,
    colDef,
    options = [],
    getOptionLabel,
    getOptionValue,
    onRowChange,
    getRowId,
    placeholder,
    disabled,
    isClearable = false,
    error,
  } = params;

  const rowId = getRowId ? getRowId(data) : data?.id;
  const field = colDef?.field;
  const labelFn = getOptionLabel || ((opt) => opt?.label ?? opt?.name);
  const valueFn = getOptionValue || ((opt) => opt?.value ?? opt?._id);

  /* Reflect the choice optimistically so the selected label appears on the same
     click instead of waiting on the parent's recalc round-trip. */
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  /* When the stored row value is a full object (e.g. a colourway from DB), extract
     the comparable key via valueFn before searching — mirrors react-select's own
     internal matching behaviour when a full object is passed as `value`. */
  const valueKey = localValue !== null && typeof localValue === 'object' ? valueFn(localValue) : localValue;
  const selected = options.find((opt) => valueFn(opt) === valueKey) ?? null;

  const handleChange = (opt) => {
    setLocalValue(opt ?? null);
    onRowChange?.(rowId, field, opt ?? null);
  };

  return (
    <div className={`cag-cell-wrap${error ? ' cag-cell-wrap--error' : ''}`}>
      <div className="cag-cell">
        <Select
          className="cag-select"
          classNamePrefix="cag-select"
          options={options}
          value={selected}
          onChange={handleChange}
          getOptionLabel={labelFn}
          getOptionValue={valueFn}
          placeholder={placeholder ?? colDef?.headerName ?? 'Dropdown'}
          isDisabled={disabled}
          isClearable={isClearable}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          styles={MENU_PORTAL_STYLES}
          menuPosition="fixed"
          menuPlacement="auto"
          menuShouldBlockScroll
        />
      </div>
      {error ? <span className="cag-cell-error">{error}</span> : null}
    </div>
  );
}

export default SelectInputCell;
